import React from 'react';
import TrialEmailContent, {
  TrialEmailContentProps,
} from '../features/marketingEmails/components/TrialEmailContent';
import { useParams } from 'react-router';

export default function TrialVideoEmailPage(): React.JSX.Element {
  const dayNumber = useParams()['day'];

  const dailyContentProps: Record<
    string,
    Omit<TrialEmailContentProps, 'day'>
  > = {
    1: {
      content: [
        "Welcome to **swimbooker+** – your exclusive pass to the UK's most rewarding angling experience.",
        'Your 7-day free trial starts today. That means:',
        `- 🎁 Entry into weekly premium giveaways
- 📈 Access to exclusive PRO catch reports
- 🎣 Specialised Angling Insurance
- 💬 Entry to the private SB+ angling community
- 🛍️ Member-only discounts on tackle and merch`,
        "And that's just the start…",
        "Over the next few days, we'll show you how to get the most out of your trial. But for now…",
        '**Explore your SB+ dashboard and unlock every perk.**',
      ],
      signOff: ['Tight lines,', '**The swimbooker Team**'],
      callToAction: '👉 Explore all your perks',
      secondaryCallToAction: null,
      actionHref: process.env.REACT_APP_MAIN_SITE + 'sb/mobile/pro-dashboard',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day1.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-1-video-noplay.png',
    },
    2: {
      content: [
        'Did you know? **Every swimbooker+ member is automatically entered into our weekly prize draw.**',
        "We're talking top-tier tackle, accessories, venue vouchers - the works. Recent prizes include:",
        `- ➡️ Carp Porter MK2 with Drop in Bag
- ➡️ Fox F Box Deluxe Set Filled to the brim with Terminal Tackle
- ➡️ RM Griddle XXL, RM Granite Toaster Stand, RM Windshield, RM Plate, Bowl & Cutlery Set`,
        'Multiple prizes are awarded each week so there an even greater chance of snagging a win! 🤩',
        "✅ As a trial user, your name's already in the hat – just click below to answer the question and confirm your entry.",
      ],
      signOff: [
        "Don't let this week's prize slip through your net. 🎣",
        'Tight lines,',
        '**The swimbooker Team**',
      ],
      callToAction: "🎁 Enter this week's giveaway",
      secondaryCallToAction: 'Confirm your free entry now →',
      actionHref: process.env.REACT_APP_MAIN_SITE + 'sb/mobile/giveaway',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day2.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-2-video-noplay.png',
    },
    3: {
      content: [
        'As a swimbooker+ member, you get **access to Pro Catch Reports** – trusted by hundreds of serious anglers.',
        `- ✔️ Where the fish are biting
- ✔️ Which baits are working
- ✔️ Venue-by-venue insight from top contributors and data trends
- ✔️ AI-Powered Weather Conditions (Including cloud cover, wind speed and direction & moon phase)`,
        "Here's a sneak peek:",
        `📍 "Pink Squid Boilies are producing multi fish hits up to almost 30lbs in Swim 6 on Top Lake at Churchgate Lakes"`,
        '🎯 Use these reports to learn more about your own fishing and track the exact scenarios and tactics that lead to more fish on the bank.',
      ],
      signOff: [
        'See it first. Fish it first. Outfish the rest.',
        'Tight lines,',
        '**The swimbooker Team**',
      ],
      callToAction: '📊 Read Pro Catch Report',
      secondaryCallToAction: 'Upload a catch report now →',
      actionHref: process.env.REACT_APP_MAIN_SITE + 'sb/mobile/catch-report',
      secondaryActionHref:
        process.env.REACT_APP_MAIN_SITE + 'sb/mobile/submit-catch',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day3.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-3-video-noplay.png',
    },
    4: {
      content: [
        "Fishing's meant to be relaxing – and with **swimbooker+**, it comes with peace of mind too.",
        "We've partnered with **Aviva** to provide active members with **built-in insurance cover** whether you've booked through swimbooker or not!",
        `- ✔️ Personal accident cover
- ✔️ Equipment protection (terms apply)`,
        "Whether you're on the bank, in the margins, or climbing out of the bivvy in the dark, we've got your back.",
        'This perk is **exclusive to SB+ members** and is already active during your trial.',
      ],
      signOff: [
        "Fish with confidence. You're covered.",
        '**– The swimbooker Team**',
      ],
      callToAction: '🛡️ View your insurance benefits',
      secondaryCallToAction: 'Check your insurance benefits →',
      actionHref: process.env.REACT_APP_MAIN_SITE + 'sb/mobile/insurance',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day4.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-4-video-noplay.png',
    },
    5: {
      content: [
        "swimbooker+ doesn't just help you catch more – it helps you spend less.",
        'Get **exclusive discounts** on:',
        `- 🎣 Tackle & rigs
- 🧥 Branded merch
- 🪣 Bait & accessories`,
        'Browse all available offers, with new items added regularly.',
      ],
      signOff: [
        "Catch more, spend less. That's the SB+ way.",
        'Tight lines,',
        '**The swimbooker Team**',
      ],
      callToAction: '🛒 Shop SB+ Discounts',
      secondaryCallToAction: 'Shop the SB+ Store Now →',
      actionHref: 'https://shop.swimbooker.com/',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day5.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-5-video-noplay.png',
    },
    6: {
      content: [
        "SB+ isn't just about perks – it's about people.",
        "As a trial user, you're invited into the **private SB+ angling community**:",
        `- 💬 Tackle tips & tactics
- 🎯 First-look features
- 🐟 Venue deep dives and Q&As
- 🙋 Exclusive member polls & beta access`,
        "It's where sharp minds, good chat, and shared passion meet. Think of it as your virtual syndicate.",
      ],
      signOff: [
        "Come say hello – we'd love to see you inside.",
        'Cheers,',
        '**The swimbooker Team**',
      ],
      callToAction: '💬 Join the SB+ Community',
      secondaryCallToAction: 'Join the community now →',
      actionHref: 'https://www.facebook.com/groups/swimbookerplus/',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day6.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-6-video-noplay.png',
    },
    7: {
      content: [
        'Your 7-day swimbooker+ trial ends **tomorrow**.',
        "Here's what you'll lose:",
        `- ❌ Weekly gear giveaway entries
- ❌ Angling Insurance
- ❌ Pro Catch Reports
- ❌ Exclusive discounts
- ❌ Member community access`,
        "Don't let it slip away. Stay subscribed from just **£5.49/month**, and keep every edge you've unlocked. Limited time promotional price.",
      ],
      signOff: [
        'Keep fishing smarter – with swimbooker+ by your side.',
        'Tight lines,',
        '**The swimbooker Team**',
      ],
      callToAction: '✅ Stay SB+',
      secondaryCallToAction: 'Stay subscribed to swimbooker+ →',
      actionHref:
        process.env.PUBLIC_URL +
        '/sign-up/post-trial-offer?interval=month&userId=',
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/Day7.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/day-7-video-noplay.png',
    },
    signup: {
      title: (
        <>
          ✅ A special welcome to swimbooker{' '}
          <strong className='font-montserrat'>PLUS</strong>
        </>
      ),
      content: [
        "Thanks for upgrading! As a new subscriber, you've unlocked a short welcome message from our co-founder, Alfie, recorded exclusively for members like you.",
        'We built swimbooker PLUS to give anglers better access, better tools, and better value. Your membership helps power that vision — and we’re just getting started.',
        "If you ever require support or have questions about your membership, please don't hesitate to contact our helpful support team! 🎣",
      ],
      signOff: ['Tight lines,', '**The swimbooker Team**'],
      videoURL:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/PostTrialSignUpWelcome.mp4',
      videoPoster:
        'https://swimbooker-fishery-details.s3.eu-west-2.amazonaws.com/web_assets/email_media/trial-conversion/signup-video-noplay.png',
    },
  };

  if (!dayNumber) {
    return (
      <div className='marketing-email-wrapper h-full w-full bg-grey-100' />
    );
  }

  return (
    <div className='marketing-email-wrapper h-full w-full bg-grey-100'>
      <TrialEmailContent
        day={dayNumber}
        {...(dailyContentProps?.[dayNumber] ?? {})}
      />
    </div>
  );
}
